import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormOptionCard from '@anwb/poncho/components/form-option-card';
import { PanelStandard } from '@anwb/poncho/components/panel';
import Typography from '@anwb/poncho/components/typography';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { setUpgrade } from 'store/actions';
import {
	chosenUpgradeSelector,
	defaultUpgradeSelector,
	isNewCarSelector,
	leaseCarHasDifferentDoorsSelector,
	leaseCarHasDifferentTransmissionsSelector,
	leaseCarUpgradesSelector,
} from 'store/selectors';
import { Upgrade as UpgradeType } from 'types';
import { trackEvent } from 'utils/trackEvent';

import { UitrustingLink } from './UitrustingLink';
import { getUpgradeSupportText } from './utils/getUpgradeSupportText';

import { FormOptionCardContainer } from './styles/upgrade.styled';

export const Upgrade = () => {
	const upgrades = useSelector(leaseCarUpgradesSelector);
	const basicUpgrade = useSelector(defaultUpgradeSelector);
	const selectedUpgrade = useSelector(chosenUpgradeSelector);
	const isNewCar = useSelector(isNewCarSelector);
	const differentTransmissions = useSelector(leaseCarHasDifferentTransmissionsSelector);
	const differentDoors = useSelector(leaseCarHasDifferentDoorsSelector);

	const [currentValue, setCurrentValue] = useState<string>(
		selectedUpgrade ? selectedUpgrade.id.toString() : basicUpgrade.id.toString()
	);
	const [availableUpgrades, setAvailableUpgrades] = useState<UpgradeType[]>([]);

	const dispatch = useDispatch();
	const gaFunnelInfo = useGaFunnelInfo();

	useEffect(() => {
		if (!selectedUpgrade) {
			dispatch(setUpgrade(basicUpgrade));
		}
		// only needs to happen once on initial load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const sortedUpgrades = [...upgrades].sort((a, b) => a.monthPrice - b.monthPrice);
		setAvailableUpgrades(sortedUpgrades);
	}, [upgrades]);

	const handleChange = (value: string | string[]) => {
		if (typeof value === 'string' && currentValue !== value) {
			setCurrentValue(value);
			const chosenUpgrade = upgrades.find((upgrade) => upgrade.id === parseInt(value, 10));
			if (chosenUpgrade) {
				dispatch(setUpgrade(chosenUpgrade));
				trackEvent({
					event: 'user_interaction',
					type: 'form',
					component: 'form-input-radiobutton',
					field: '',
					action: 'change',
					actionValue: '',
					category: 'version',
					variant: chosenUpgrade.description,
					...gaFunnelInfo,
				});
			}
		}
	};

	return isNewCar ? (
		<PanelStandard data-test="upgrade" hasOuterSpacing={false}>
			<Typography variant="component-title">Uitvoering</Typography>
			<FormOptionCardContainer>
				<FormOptionCard
					name="upgrade"
					dataTest="upgrade"
					inputType="radio"
					columns={2}
					onChange={handleChange}
					preselectedOptions={[currentValue]}
					required
				>
					{availableUpgrades.map((upgrade) => {
						const supportText = getUpgradeSupportText(
							upgrade,
							differentTransmissions,
							differentDoors
						);

						return (
							<FormOptionCard.Item
								key={upgrade.id}
								label={upgrade.description}
								value={upgrade.id.toString()}
								subTitle={
									upgrade.monthPrice ? `€ ${upgrade.monthPrice} per maand` : 'Geen extra kosten'
								}
								subTitleSize="small"
							>
								{supportText && (
									<Typography variant="support-text" dataTest="doors-and-transmission">
										{supportText}
									</Typography>
								)}

								<UitrustingLink upgrade={upgrade} />
							</FormOptionCard.Item>
						);
					})}
				</FormOptionCard>
			</FormOptionCardContainer>
		</PanelStandard>
	) : null;
};
